import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { User, Users, Building2, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONA_META = {
  individual: {
    icon: User,
    title: "Individual",
    blurb: "Solo developer, researcher, or trader exploring QorTrace for personal use.",
  },
  team: {
    icon: Users,
    title: "Team",
    blurb: "Startup or small org adopting QorTrace across a few teammates.",
  },
  enterprise: {
    icon: Building2,
    title: "Enterprise",
    blurb: "Institution evaluating QorTrace for production-grade deployment or custom build.",
  },
};

/**
 * PersonaStep — shared persona wizard UI, used by:
 *   • The Phase 1 multi-step signup (atomic with account creation).
 *   • The soft-prompt modal that nudges existing users to set persona.
 *
 * Props:
 *   onSubmit(payload) — async; payload is the validated persona object.
 *   onSkip          — optional; if provided, renders a "Remind me later"
 *                     button (used by the soft-prompt modal, not signup).
 *   mandatory       — when true, "Skip" is hidden / disabled.
 *   submitLabel     — optional override for the primary CTA copy.
 *   busy            — externally-controlled loading state.
 *   error           — externally-supplied error string.
 *   testid          — base data-testid prefix for individual fields.
 */
export default function PersonaStep({
  onSubmit,
  onSkip,
  mandatory = false,
  submitLabel,
  busy: extBusy = false,
  error: extError = "",
  testid = "persona",
}) {
  const [opts, setOpts] = useState(null);
  const [persona_type, setPersonaType] = useState("");
  const [role, setRole] = useState("");
  const [company_name, setCompanyName] = useState("");
  const [company_size, setCompanySize] = useState("");
  const [enterprise_track, setTrack] = useState("");
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/account/persona/options`).then((r) => {
      if (alive) setOpts(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Reset role + company fields when persona_type changes so we never
  // submit a stale combination.
  useEffect(() => {
    setRole("");
    if (persona_type === "individual") {
      setCompanyName("");
      setCompanySize("");
      setTrack("");
    } else if (persona_type === "team") {
      setTrack("");
    }
  }, [persona_type]);

  const roleList = useMemo(() => {
    if (!opts || !persona_type) return [];
    return opts.roles?.[persona_type] || [];
  }, [opts, persona_type]);

  const validate = () => {
    if (!persona_type) return "Select a persona type to continue.";
    if (!role) return "Choose your role.";
    if (persona_type !== "individual") {
      if (!company_name.trim()) return "Company name is required.";
      if (!company_size) return "Company size is required.";
    }
    if (persona_type === "enterprise" && !enterprise_track) {
      return "Choose subscription or custom build.";
    }
    return "";
  };

  const submit = async (e) => {
    e?.preventDefault();
    const err = validate();
    if (err) { setLocalError(err); return; }
    setLocalError("");
    const payload = {
      persona_type,
      role,
      company_name: persona_type === "individual" ? null : company_name.trim(),
      company_size: persona_type === "individual" ? null : company_size,
      enterprise_track: persona_type === "enterprise" ? enterprise_track : null,
    };
    await onSubmit(payload);
  };

  const error = extError || localError;
  const busy = extBusy;

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-5" data-testid={`${testid}-form`}>
      {/* Persona type — three large select cards */}
      <div>
        <div className="qt-label mb-3">WHO IS THIS ACCOUNT FOR?</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          {Object.entries(PERSONA_META).map(([key, meta]) => {
            const active = persona_type === key;
            const Icon = meta.icon;
            return (
              <button
                type="button"
                key={key}
                data-testid={`${testid}-type-${key}`}
                onClick={() => setPersonaType(key)}
                className="text-left p-4 rounded-lg border transition-colors"
                style={{
                  background: active ? "rgba(122,231,242,0.08)" : "rgba(122,231,242,0.02)",
                  borderColor: active ? "#7AE7F2" : "rgba(122,231,242,0.18)",
                }}
              >
                <Icon size={18} style={{ color: active ? "#7AE7F2" : "rgba(122,231,242,0.6)" }} />
                <div className="text-sm font-semibold text-white mt-2">{meta.title}</div>
                <div className="text-[11px] mt-1 leading-snug" style={{ color: "rgba(255,255,255,0.55)" }}>
                  {meta.blurb}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Role — depends on persona_type */}
      {persona_type && (
        <div>
          <div className="qt-label mb-2">YOUR ROLE</div>
          <select
            className="qt-input"
            data-testid={`${testid}-role`}
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="">Select a role…</option>
            {roleList.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
      )}

      {/* Company info — team & enterprise only */}
      {persona_type && persona_type !== "individual" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="qt-label mb-2">COMPANY NAME</div>
            <input
              className="qt-input"
              data-testid={`${testid}-company-name`}
              value={company_name}
              maxLength={160}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Acme Capital"
            />
          </div>
          <div>
            <div className="qt-label mb-2">COMPANY SIZE</div>
            <select
              className="qt-input"
              data-testid={`${testid}-company-size`}
              value={company_size}
              onChange={(e) => setCompanySize(e.target.value)}
            >
              <option value="">Select…</option>
              {(opts?.company_sizes || []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Enterprise track fork */}
      {persona_type === "enterprise" && (
        <div>
          <div className="qt-label mb-2">WHAT BRINGS YOU TO QORTRACE?</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <TrackOption
              label="Subscribe to QorTrace"
              description="Standard enterprise SKU for your team's day-to-day use."
              active={enterprise_track === "subscription"}
              onClick={() => setTrack("subscription")}
              testid={`${testid}-track-subscription`}
            />
            <TrackOption
              label="Custom build / engagement"
              description="You need us to build or extend infrastructure for you."
              active={enterprise_track === "custom_build"}
              onClick={() => setTrack("custom_build")}
              testid={`${testid}-track-custom-build`}
            />
          </div>
        </div>
      )}

      {error && (
        <div
          className="px-3 py-2 rounded text-sm"
          data-testid={`${testid}-error`}
          style={{
            background: "rgba(255,42,42,0.08)",
            border: "1px solid rgba(255,42,42,0.4)",
            color: "#FF6E6E",
          }}
        >
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 mt-1">
        <button
          type="submit"
          disabled={busy}
          data-testid={`${testid}-submit`}
          className="qt-btn qt-btn-primary"
        >
          {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> :
            <>{submitLabel || "Continue"} <ArrowRight size={14} /></>}
        </button>
        {onSkip && !mandatory && (
          <button
            type="button"
            disabled={busy}
            data-testid={`${testid}-skip`}
            onClick={onSkip}
            className="qt-mono text-sm"
            style={{ color: "var(--qt-text-3)", letterSpacing: "0.05em" }}
          >
            Remind me later
          </button>
        )}
      </div>
    </form>
  );
}

function TrackOption({ label, description, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left p-4 rounded-lg border transition-colors"
      style={{
        background: active ? "rgba(122,231,242,0.08)" : "rgba(122,231,242,0.02)",
        borderColor: active ? "#7AE7F2" : "rgba(122,231,242,0.18)",
      }}
    >
      <div className="text-sm font-semibold text-white">{label}</div>
      <div className="text-[11px] mt-1 leading-snug" style={{ color: "rgba(255,255,255,0.55)" }}>
        {description}
      </div>
    </button>
  );
}
